import { Ellipsis, RefreshCw, Target } from 'lucide-react';
import { Link } from 'react-router';
import type { AssistantPendingDTO } from '@contracts/assistant';
import type { RunCardDTO } from '@contracts/assistant-proactive';
import type { Id } from '@contracts/common';
import { BUSINESS_TIME_ZONE, businessDate, daysBetween } from '@domain/dates';
import { Button } from '../../../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { useNarrowView } from '../../../lib/media';
import { useAssistantOptional } from '../AssistantProvider';
import { ConfirmCard, type ConfirmEdit } from '../ConfirmCard';
import { Rich } from '../MessageList';
import { missionHref, orderHref } from '../missionLinks';
import { ORDER_OFF, STALE_RUN } from '../missions/labels';
import { ObjectChip, refKey, useResolved } from '../ObjectChip';
import { EXT_CARD_LABELS } from '../ext-card-labels';
import { CHIP } from './NudgeRow';
import { DEFAULT_PERSONA_NAME } from '@contracts/persona';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * How long is left, by the day (S7 §9.3, mockup §4): "còn 2 ngày 7 giờ" while a day or more is left, then "còn 7 giờ",
 * then minutes. Null once it is over (the server drops the card on its next read).
 */
export function leftText(untilIso: string, now: Date = new Date()): string | null {
  const ms = new Date(untilIso).getTime() - now.getTime();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const days = Math.floor(ms / DAY);
  const hours = Math.floor((ms % DAY) / HOUR);
  if (days > 0) return hours > 0 ? `còn ${days} ngày ${hours} giờ` : `còn ${days} ngày`;
  if (hours > 0) return `còn ${hours} giờ`;
  return `còn ${Math.max(1, Math.floor(ms / MINUTE))} phút`;
}

const CLOCK = new Intl.DateTimeFormat('vi-VN', { timeZone: BUSINESS_TIME_ZONE, hour: '2-digit', minute: '2-digit' });
const DAY_MONTH = new Intl.DateTimeFormat('vi-VN', { timeZone: BUSINESS_TIME_ZONE, day: '2-digit', month: '2-digit' });

/** When the run happened, in the business day (mockup §4): "08:01", "hôm qua 16:20", else "03/10 16:20". */
export function runAtText(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const days = daysBetween(businessDate(at), businessDate(now));
  const clock = CLOCK.format(at);
  if (days <= 0) return clock;
  if (days === 1) return `hôm qua ${clock}`;
  return `${DAY_MONTH.format(at)} ${clock}`;
}

const TOKENS = new Intl.NumberFormat('vi-VN');
/** The spec's defaults (§11's example): what a card from a server before D107 is read with. */
const DEFAULT_RUN_TOKEN_CAP = 60_000;
const DEFAULT_TOKEN_BUDGET = 1_000_000;
/** Why a run stopped, in the spec's words (§11), with the run's own limits (D107); any other code is its own error. */
const RUN_ERROR_TEXT: Record<string, (limits: RunLimits) => string> = {
  RUN_TOKEN_CAP: (l) => `Dừng vì vượt ${TOKENS.format(l.runTokenCap ?? DEFAULT_RUN_TOKEN_CAP)} token của một lượt.`,
  MISSION_BUDGET: (l) =>
    `Nhiệm vụ đã dùng hết ${TOKENS.format(l.tokenBudget ?? DEFAULT_TOKEN_BUDGET)} token. Nâng ngân sách để tiếp tục.`,
  ASSISTANT_QUOTA: () => 'Hết hạn mức hôm nay, mai em làm tiếp.',
  RUN_TIMEOUT: () => 'Lượt chạy quá 5 phút nên dừng.',
  RUN_ACCESS_LOST: () => 'Tạm dừng vì tài khoản của anh không dùng trợ lý được.',
  RUN_STALLED: () => 'Lượt chạy bị gián đoạn.',
};
const RUN_ERROR = 'Lượt chạy gặp lỗi.';
type RunLimits = { runTokenCap?: number | null; tokenBudget?: number | null };
export const runErrorText = (code: string, limits: RunLimits = {}): string =>
  RUN_ERROR_TEXT[code]?.(limits) ?? RUN_ERROR;
/** D97: a run its owner answered, queued or running again (§9.3 has no words for it). */
const resuming = (persona: string): string => `${persona} đang làm tiếp…`;
/** D103: a cancelled run's cards can no longer be acted on (D46). */
const STOPPED = 'Lượt chạy nền này đã dừng';
const EXPIRED = EXT_CARD_LABELS.expired;

export { missionHref } from '../missionLinks';

const MUTED = 'text-[hsl(var(--h-muted-foreground))]';

function Touched({ card }: { card: RunCardDTO }) {
  const resolved = useResolved(card.objects);
  if (card.objects.length === 0) return null;
  return (
    <p className="mt-1.5 flex flex-wrap items-center gap-1 text-xs">
      {/* D106: on their own row the chips leave the inline-text exemption, so a touch layout gives each 44 px. */}
      {card.objects.map((o) => (
        <span key={refKey(o)} data-touch-hit="" className="inline-flex min-h-11 items-center lg:min-h-0">
          <ObjectChip objectRef={o} resolved={resolved.get(refKey(o))} />
        </span>
      ))}
    </p>
  );
}

export type RunCardHandlers = {
  busy: (id: Id) => boolean;
  onAct: (runId: Id, actionId: Id, kind: 'confirm' | 'reject') => void;
  edit: (runId: Id, pending: AssistantPendingDTO) => ConfirmEdit;
  onUndo: (card: RunCardDTO) => void;
  onDismiss: (card: RunCardDTO) => void;
};

/**
 * One background run in Hana's tray (S7 §9.3, mockup §4 `.ti`): the order's ↻ or the mission's ◎ with its name and
 * when it ran, "⋯" to hide it (Ẩn), what Hana did, the objects it touched, the undo strip while its window is open, each
 * card still waiting (ConfirmCard on the run's routes, counted down by the day) and, for a question, the way to the
 * Nhiệm vụ page.
 */
export function RunCard({ card, now, handlers, onPage = false }: {
  card: RunCardDTO; now: Date; handlers: RunCardHandlers;
  /** Shown on the mission's own page: no link to that same page. */
  onPage?: boolean;
}) {
  const { owner } = card;
  // D136: the mission stopped because its goal card was deleted — nothing on the card can go on; the page says what next.
  const stale = owner.kind === 'mission' && card.missionStatus === 'paused' && card.pauseReason === 'stale';
  const Icon = owner.kind === 'order' ? RefreshCw : Target;
  const busy = handlers.busy(card.runId);
  const undoLeft = card.undo === null ? null : leftText(card.undo.until, now);
  const assistant = useAssistantOptional();
  const narrow = useNarrowView();
  return (
    <li data-run-card={card.runId} data-tray-entry={`run:${card.runId}`}
      className="mt-1.5 rounded-lg border border-[hsl(var(--h-border))] bg-[hsl(var(--h-card))] px-2.5 py-2 text-[13px] leading-[1.45] text-[hsl(var(--h-card-foreground))] first:mt-0">
      <div className="flex items-center gap-2">
        <span role="img" aria-label={owner.kind === 'order' ? 'Lệnh thường trực' : 'Nhiệm vụ'}
          className="flex size-[18px] flex-none items-center justify-center rounded-[5px] bg-[hsl(var(--h-tone-violet-chip-bg))] text-[hsl(var(--h-tone-violet-chip-fg))]">
          <Icon className="size-3" aria-hidden="true" />
        </span>
        <p className="m-0 min-w-0 flex-1 font-bold wrap-anywhere">{`${owner.title} · ${runAtText(card.at, now)}`}</p>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" data-tray-focus="" aria-label="Tuỳ chọn lượt chạy" title="Tuỳ chọn lượt chạy" disabled={busy}
              className="flex size-11 flex-none items-center justify-center rounded-md text-[hsl(var(--h-muted-foreground))] transition-colors hover:bg-[hsl(var(--h-muted))] lg:size-6">
              <Ellipsis className="size-4" aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[150px]">
            <DropdownMenuItem onSelect={() => handlers.onDismiss(card)}>Ẩn</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {card.summary !== null && card.summary !== '' && (
        <div className="mt-1 [&_p]:m-0"><Rich text={card.summary} /></div>
      )}
      {card.errorCode !== null && card.errorCode !== 'ORDER_OFF' && (
        <p className={`m-0 mt-1 ${MUTED}`}>{runErrorText(card.errorCode, card)}</p>
      )}
      {/* L4 P5: the order went off by itself (D181: the carrier run has nothing else to say). */}
      {card.orderOff != null && (
        <div data-run-order-off="" className="mt-1 flex flex-col items-start gap-1.5">
          <p className={`m-0 ${MUTED}`}>{ORDER_OFF.line(card.orderOff)}</p>
          {/* C2: the order is turned back on from its own row, as a stale mission is opened from its page. */}
          {owner.kind === 'order' && !onPage && (
            <Link to={orderHref(owner.id)} className={CHIP}
              onClick={() => { if (narrow) assistant?.setOpen(false); }}>{ORDER_OFF.open}</Link>
          )}
        </div>
      )}
      {(card.status === 'queued' || card.status === 'running') && (
        <p role="status" className={`m-0 mt-1 ${MUTED}`}>{resuming(assistant?.prefs.personaName ?? DEFAULT_PERSONA_NAME)}</p>
      )}
      <Touched card={card} />
      {stale && (
        <div data-run-stale="" className="mt-1.5 flex flex-col items-start gap-1.5">
          <p className={`m-0 ${MUTED}`}>{STALE_RUN.text(assistant?.prefs.personaName ?? DEFAULT_PERSONA_NAME)}</p>
          {!onPage && (
            <Link to={missionHref(owner.id)} className={CHIP}
              onClick={() => { if (narrow) assistant?.setOpen(false); }}>{STALE_RUN.open}</Link>
          )}
        </div>
      )}
      {!stale && card.undo !== null && undoLeft !== null && (
        <div data-run-undo=""
          className="mt-1.5 flex flex-wrap items-center gap-2 rounded-lg bg-[hsl(var(--h-muted))] px-2.5 py-1.5 text-xs">
          <span className="min-w-0 flex-1 break-words">{card.undo.summary}</span>
          <Button type="button" variant="outline" disabled={busy}
            className="h-auto min-h-11 rounded-[5px] bg-[hsl(var(--h-card))] px-2 py-[3px] text-xs lg:min-h-0"
            onClick={() => handlers.onUndo(card)}>
            Hoàn tác
          </Button>
          <span className={MUTED}>{undoLeft}</span>
        </div>
      )}
      {!stale && card.status === 'cancelled' && card.pending.length > 0 && (
        <div data-run-stopped="" className="mt-1.5 rounded-lg border border-[hsl(var(--h-border))] px-2.5 py-1.5 text-xs">
          <ul className="m-0 list-none p-0">
            {card.pending.map((p) => <li key={p.actionId} className="break-words">{p.summary}</li>)}
          </ul>
          <p className={`m-0 mt-1 ${MUTED}`}>{STOPPED}</p>
        </div>
      )}
      {!stale && card.status !== 'cancelled' && card.pending.map((p) => {
        const left = leftText(p.expiresAt, now);
        return (
          <div key={p.actionId} className="mt-1.5 flex flex-col gap-1">
            {/* D103: an expired card is closed, as in the chat (D52): "đã hết hạn", nothing to press. */}
            <ConfirmCard pending={p} disabled={left === null || handlers.busy(p.actionId)} className="max-w-none"
              onAct={(actionId, kind) => handlers.onAct(card.runId, actionId, kind)}
              edit={handlers.edit(card.runId, p)} aside={left ?? EXPIRED} />
            {/* An outside call's card has its own actions row: the countdown sits under it. */}
            {p.external !== undefined && left !== null && <p className={`m-0 text-right text-xs ${MUTED}`}>{left}</p>}
          </div>
        );
      })}
      {!stale && !onPage && card.asked && owner.kind === 'mission' && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {/* The phone sheet covers the page: it steps aside, as an object chip's link does. */}
          <Link to={missionHref(owner.id)} className={CHIP}
            onClick={() => { if (narrow) assistant?.setOpen(false); }}>Trả lời ở trang Nhiệm vụ</Link>
        </div>
      )}
    </li>
  );
}
